import type { WithStyles, Theme } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { QuoteArea, QuoteDocument, QuoteFile, QuoteSource } from 'paintscout'
import type {
  UpdateableAreaSubstrate,
  QuoteItemSection,
  UpdateableArea,
  RenderableItemSubstrate,
  LabelsOption,
  QuoteItemConsumer,
  EditableRate
} from '@paintscout/util/builder'
import {
  copyAreaSubstrate,
  getEditableRateUpdateableProducts,
  getAreaSubstrate,
  createAreaSubstrate,
  getArea,
  getAreaDescription,
  keyAreaSubstrates,
  moveAreaSubstrate,
  removeAreaSubstrate,
  reorderAreaSubstrates,
  updateArea,
  getFeatures,
  updateAreaSubstrate,
  updateFilesForItem,
  getFilesForItem,
  getFiles,
  uuid,
  getObjectLabels,
  getAreaSubstrates,
  hasIntegrationInterface,
  getItemSection
} from '@paintscout/util/builder'
import DescriptionIcon from '@material-ui/icons/Description'
import React from 'react'
import type { WithClientOptions, WithDialogStackContext, DialogProps, WithUser, DialogToggleItem } from '@paintscout/ui'
import {
  Button,
  Dialog,
  AlertDialog,
  ConfirmationDialog,
  withClientOptions,
  withDialogStackContext,
  DialogActions,
  DialogActionArrows,
  DialogContent,
  DialogToggles,
  withUser,
  CompanyCamLogo
} from '@paintscout/ui'
import classnames from 'classnames'
import MediaIcon from '@material-ui/icons/PermMedia'
import type { WithSnackbarProps } from 'notistack'
import { withSnackbar } from 'notistack'
import { Detector } from 'react-detect-offline'
import get from 'lodash/get'
import clone from 'lodash/clone'
import { isLooselyEqual, TRIAL_QUOTE_FILES_LIMIT, findNextKey, findPreviousKey } from '@paintscout/util'

import { calculate } from '@paintscout/util/calculator'
import EditItemFilesDialog from '../../dialogs/EditItemFilesDialog'
import RateSelectDialog from '../RateSelectDialog'
import EditAreaSubstrateDialog from '../EditAreaSubstrateDialog'
import EditIntegrationItemFilesDialog from '../EditIntegrationItemFilesDialog'
import EditAreaDialogHeader from './EditAreaDialogHeader'
import AreaTotalsDialog from './AreaTotalsDialog'
import AreaMainView from './AreaMainView'
import AreaDetailsDialog from './AreaDetailsDialog'
import AreaNotesDialog from './AreaNotesDialog'

export interface EditAreaDialogProps extends DialogProps {
  quote: QuoteDocument
  consumer: QuoteItemConsumer
  item?: UpdateableArea
  isNew?: boolean
  isInGroup?: boolean
  section?: QuoteItemSection
  onConfirm: (quote: QuoteDocument, itemKey?: string) => void
  onCancel: (quote: QuoteDocument) => void
  cancelButtonLabel?: string
  nextItemKey?: string
  previousItemKey?: string
}

export type EditAreaDialogPropsWithInjections = EditAreaDialogProps &
  WithStyles<typeof styles> &
  WithClientOptions &
  WithSnackbarProps &
  WithDialogStackContext &
  WithUser

export interface EditAreaDialogState {
  // normally we could refer to props.quote as the clean quote, but we run keyAreaSubstrates during the constructor
  cleanQuote: QuoteDocument
  quote: QuoteDocument
  itemKey: string
  isNew?: boolean
  quoteLabel?: LabelsOption
  section?: QuoteItemSection

  /**
   * The total number of quote files excluding this area (so we can calculate how many files a trial user can add)
   */
  numQuoteFiles?: number
  /**
   * Track if there were unsaved changes in AreaDetailsDialog when it was closed
   */
  areaDetailsHadChanges?: boolean
}

export const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    content: {
      // paddingTop: 0,
      // paddingBottom: 0
      padding: `0 ${theme.spacing(1)}px`
    },
    contentInner: {
      // padding: `0 ${pxToRem(10)}`
    },
    dialogToggles: {
      justifyContent: 'center'
    }
  })
}

class EditAreaDialog extends React.PureComponent<EditAreaDialogPropsWithInjections, EditAreaDialogState> {
  private areaDetailsHasChangesRef = React.createRef<(() => boolean) | null>()

  constructor(props: EditAreaDialogPropsWithInjections) {
    super(props)
    const { item, clientOptions, section } = this.props
    const cleanQuote = keyAreaSubstrates({ quote: this.props.quote, areaKey: item.key })
    const quote = clone(cleanQuote)
    const files = getFilesForItem({ quote, itemKey: item.key, options: clientOptions.options })
    this.state = {
      quote: quote,
      itemKey: item.key,
      cleanQuote,
      isNew: props.isNew,
      numQuoteFiles: getFiles({ quote, attached: true }).length - files.length,
      quoteLabel: getObjectLabels({ options: clientOptions.options, invoice: cleanQuote.is_invoice }),
      section
    }
  }

  public componentDidMount() {
    if (this.state.isNew) {
      this.handleEditDetails()
    }
  }

  public render() {
    const {
      open,
      loading: _loading,
      classes,
      onConfirm: _onConfirm,
      nextItemKey,
      previousItemKey,
      dialogStackContext: _dialogStackContext,
      clientOptions: _clientOptions,
      item,
      enqueueSnackbar: _equeueSnackbar,
      closeSnackbar: _closeSnackbar,
      isNew: _isNew,
      isInGroup: _isInGroup,
      user,
      ...baseDialogProps
    } = this.props
    const { options } = this.props.clientOptions
    const { quote, itemKey, numQuoteFiles, section } = this.state
    const files = getFilesForItem({ quote: quote, options, itemKey: item.key })
    const area = {
      ...getArea({ quote, options, key: itemKey, trimBySubstrateSection: 'bid' }),
      description: getAreaDescription({ quote, options, areaKey: itemKey, section })
    }
    const features = getFeatures({ options })
    const mediaFeature = get(features, 'media.enabled', true)

    const canAddFiles = !user?.isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT
    const canEditTotals = options?.options?.features?.quotes?.editableAreaTotals ?? false
    // const canEditTotals = true

    return (
      <Dialog open={open} onClose={this.handleDialogCancel} maxWidth={'lg'} fullWidth={true} {...baseDialogProps}>
        <EditAreaDialogHeader quote={quote} area={area} section={section} showEdit onEdit={this.handleEditDetails} />
        <DialogContent classes={{ root: classnames(classes.content, 'dialogContent') }}>
          {area && (
            <AreaMainView
              quote={quote}
              options={options}
              area={area}
              onAddClick={this.handleNewSubstrate}
              onItemActionClick={this.handleActionItemClick}
              onItemClick={this.handleSubstrateClick}
              onTotalsClick={canEditTotals ? this.handleEditTotals : null}
              onReorder={this.handleReorder}
              onFilesChange={this.handleFilesChange}
              classes={{ root: classes.contentInner }}
            />
          )}
        </DialogContent>

        <Detector>
          {(online) => {
            const toggles: DialogToggleItem[] = [
              mediaFeature && {
                key: 'showMedia',
                label: 'Add Media',
                icon: MediaIcon,
                disabled: !online || !canAddFiles,
                subItems: hasIntegrationInterface({ options, integration: 'companycam' })
                  ? [
                      {
                        key: 'companycam',
                        label: 'CompanyCam',
                        value: true,
                        icon: CompanyCamLogo
                      },
                      {
                        key: 'device',
                        label: 'My Device',
                        value: true,
                        icon: MediaIcon
                      }
                    ]
                  : undefined
              },
              { key: 'notes', label: 'Add Notes', icon: DescriptionIcon }
            ].filter(Boolean)

            return (
              <DialogToggles
                className={classes.dialogToggles}
                toggles={toggles}
                onToggle={this.handleToggle}
                scroll
                keepLabelsOnMobile
              />
            )
          }}
        </Detector>

        <DialogActions
          leftButton={
            <Button onClick={this.handleDialogCancel} variant="text">
              {this.props.cancelButtonLabel || 'Cancel'}
            </Button>
          }
          arrows={
            <DialogActionArrows
              hidden={this.props.isNew || (!previousItemKey && !nextItemKey)}
              onPrevious={previousItemKey ? () => this.handleNavArrowClick(previousItemKey) : null}
              onNext={nextItemKey ? () => this.handleNavArrowClick(nextItemKey) : null}
            />
          }
        >
          <Button
            type="submit"
            variant={'contained'}
            onClick={this.handleDialogConfirm}
            className={'tour-edit-area-confirm-button'}
          >
            {this.props.isNew ? 'Add Area' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  public componentDidUpdate(prevProps: EditAreaDialogPropsWithInjections, prevState: EditAreaDialogState) {
    const { options } = this.props.clientOptions
    const { section } = this.state
    if (this.props.item !== prevProps.item) {
      const { item, clientOptions, section, isNew } = this.props
      const cleanQuote = keyAreaSubstrates({ quote: this.props.quote, areaKey: item.key })
      const quote = clone(cleanQuote)
      const files = getFilesForItem({ quote, itemKey: item.key, options: clientOptions.options })
      this.setState({
        quote: quote,
        itemKey: item.key,
        cleanQuote,
        isNew: isNew,
        numQuoteFiles: getFiles({ quote, attached: true }).length - files.length,
        quoteLabel: getObjectLabels({ options: clientOptions.options, invoice: cleanQuote.is_invoice }),
        section
      })
    } else {
      const prevDescription = getAreaDescription({
        quote: prevState.quote,
        options,
        areaKey: this.state.itemKey,
        plainText: false,
        section
      })
      const newDescription = getAreaDescription({
        quote: this.state.quote,
        options,
        areaKey: this.state.itemKey,
        plainText: false,
        section
      })

      // Show a warning  that there would be a change in default area description if they weren't using custom
      if (prevDescription.default !== newDescription.default && newDescription.useCustom) {
        this.props.enqueueSnackbar('Your custom Area Description may need to be updated to reflect this change.', {
          id: 'toast-new-default-description',
          variant: 'warning'
        })
      }
    }
  }

  public handleEditDetails = () => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions
    const { consumer } = this.props
    const { itemKey, section } = this.state
    const area = {
      ...getArea({ quote: this.state.quote, options, key: itemKey }),
      description: getAreaDescription({ quote: this.state.quote, options, areaKey: itemKey, plainText: false, section })
    }

    // Reset areaDetailsHadChanges when opening the dialog
    this.setState({ areaDetailsHadChanges: false })

    openDialog(AreaDetailsDialog, {
      area,
      consumer,
      maxWidth: 'lg',
      fullWidth: true,
      section,
      short: this.state.isNew,
      hasChangesRef: this.areaDetailsHasChangesRef,
      onConfirm: (newArea) => {
        const { quote } = this.state
        const isAreaEqual = isLooselyEqual(area, newArea)
        const hadChanges = !isAreaEqual
        if (isAreaEqual && !this.state.isNew) {
          dismissDialog()
          this.setState({ areaDetailsHadChanges: false })
        } else {
          const updatedQuote = updateArea({ quote, options, area: newArea })
          dismissDialog()

          this.setState(
            {
              quote: updatedQuote,
              areaDetailsHadChanges: hadChanges
            },
            () => {
              if (this.state.isNew) {
                this.handleNewSubstrate()
              }
            }
          )
        }
      },
      onCancel: () => {
        dismissDialog()
        this.setState({ areaDetailsHadChanges: false })

        // dismiss the area dialog too if it's a new area
        if (this.state.isNew) {
          dismissDialog()
        }
      }
    })
  }

  public handleFilesChange = (files: QuoteFile[], source?: QuoteSource, append?: boolean) => {
    const { itemKey } = this.state
    const { options } = this.props.clientOptions

    const updatedQuote = updateFilesForItem({
      quote: this.state.quote,
      options,
      itemKey,
      files,
      append: typeof append === 'undefined' ? true : append
    })

    if (source) {
      updatedQuote.sources = {
        ...updatedQuote.sources,
        [source.provider]: source
      }
    }

    this.setState({
      quote: updatedQuote
    })
  }

  public handleEditTotals = () => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions
    const { itemKey, quoteLabel } = this.state

    openDialog(AreaTotalsDialog, {
      quote: this.state.quote,
      areaKey: itemKey,
      quoteLabel,
      options,
      maxWidth: 'lg',
      fullWidth: true,
      onConfirm: (updatedQuote) => {
        this.setState({
          quote: updatedQuote
        })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  public handleNewSubstrate = (
    event?: React.MouseEvent<HTMLElement>,
    section?: string,
    quoteSectionProps?: QuoteItemSection
  ) => {
    const { itemKey, section: quoteSectionState } = this.state
    const quoteSection = quoteSectionProps ?? quoteSectionState ?? null
    const { options } = this.props.clientOptions
    const area = getArea({ quote: this.state.quote, options, key: itemKey })
    const quoteType = this.state?.quote?.int_ext?.value ?? 'all'
    const isTemplate = this.state?.quote?.type === 'quoteTemplate'

    this.props.dialogStackContext.openDialog(RateSelectDialog, {
      /*
        This will likely change (again)... But we broke people's ability
        to break up rates by quote type on each area _while building quotes_.

        Of course, now it won't be quite right - as you can build a template
        that has rates from other quote types... But I think that's better
        than what we created/broke (#8752, #8954)
      */
      quoteType: !isTemplate ? area.quoteType : quoteType,
      quote: this.state.quote,
      section: quoteSection,
      maxWidth: 'lg',
      fullWidth: true,
      checkParentHasChanges: () => {
        // Check if there were changes in AreaDetailsDialog when it was closed
        return this.state.areaDetailsHadChanges === true
      },
      onConfirm: (rate: EditableRate, fields?: any) => {
        const { options } = this.props.clientOptions
        const areaKey = this.state.itemKey

        const defaultProducts = getEditableRateUpdateableProducts({
          rate,
          options
        })

        const quantity = fields && fields.quantity ? fields.quantity : 1

        const hours = {
          default: 0,
          useCustom: false,
          custom: 0
        }
        const price = {
          default: 0,
          useCustom: false,
          custom: 0
        }

        if (fields && fields.hours) {
          hours.default = fields.hours
        }
        if (fields && fields.price) {
          price.default = fields.price
        }

        const substrateKey = uuid()
        const areaSubstrate = {
          ...createAreaSubstrate({
            rate,
            products: defaultProducts,
            quoteSection,
            options
          }),
          key: substrateKey,
          quantity,
          hours,
          price
        }

        const updatedQuote = updateAreaSubstrate({
          quote: this.state.quote,
          options,
          areaKey,
          areaSubstrate
        })

        this.props.dialogStackContext.dismissDialog()

        // scroll dialog to new item
        setTimeout(() => {
          const row = document.querySelector(`#item-${substrateKey}`)
          if (row) {
            row.scrollIntoView({ behavior: 'smooth' })
          }
        })

        this.setState({ quote: updatedQuote, isNew: false }, () => {
          // openSubstrateDialog relies on state.quote
          if (rate.name === 'custom') {
            this.openSubstrateDialog(areaSubstrate.key, null, true)
          }
        })
      },
      onCancel: () => {
        this.props.dialogStackContext.dismissDialog()

        // dismiss the area dialog too if it's a new area
        if (this.state.isNew) {
          this.props.dialogStackContext.dismissDialog()
        }
      }
    })
  }

  public openSubstrateDialog = (key: string, quoteArg?: QuoteDocument, initialCustom?: boolean) => {
    const areaKey = this.state.itemKey
    const quote = quoteArg ?? this.state.quote
    const { options } = this.props.clientOptions
    const { openDialog, dismissDialog } = this.props.dialogStackContext

    const item = getAreaSubstrate({ quote, options, areaKey, key }) as UpdateableAreaSubstrate
    const items: RenderableItemSubstrate[] = getAreaSubstrates({
      quote,
      options,
      areaKey,
      section: item?.quoteSection ?? getItemSection(item)
    })

    const itemKeys = items.map((item) => item.key)
    const nextKey = findNextKey(key, itemKeys)
    const prevKey = findPreviousKey(key, itemKeys)

    openDialog(EditAreaSubstrateDialog, {
      quote,
      areaKey,
      substrateKey: key,
      maxWidth: 'lg',
      fullWidth: true,
      onConfirm: (updatedQuote: QuoteDocument, navItemKey?: string) => {
        this.setState({ quote: updatedQuote })
        dismissDialog()
        if (navItemKey) {
          this.props.dialogStackContext.stack = this.props.dialogStackContext.stack.slice(0, this.props.isNew ? 2 : 1)
          this.openSubstrateDialog(navItemKey, updatedQuote)
        }
      },
      onCancel: () => {
        // Intial edit of custom substrate, if cancel we need to remove it
        if (initialCustom) {
          const updatedQuote = removeAreaSubstrate({
            quote,
            options,
            areaKey,
            key
          })
          this.setState({ quote: updatedQuote })
        }
        this.setState({ quote })
        dismissDialog()
      },
      nextItemKey: nextKey,
      previousItemKey: prevKey
    })
  }

  public handleToggle = (key: string) => {
    if (key === 'notes') {
      this.handleNotesClick()
    } else {
      this.handleFileClick(key)
    }
  }

  public handleNotesClick = () => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions
    const { itemKey } = this.state
    const area = getArea({ quote: this.state.quote, options, key: itemKey })

    openDialog(AreaNotesDialog, {
      area,
      onConfirm: (newArea: UpdateableArea) => {
        const { quote } = this.state
        const updatedQuote = updateArea({ quote, options, area: newArea })
        dismissDialog()
        this.setState({
          quote: updatedQuote
        })
      },
      onCancel: dismissDialog
    })
  }

  public handleFileClick = (key: string) => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions
    const { quote, itemKey } = this.state
    const files = getFilesForItem({ quote, itemKey, options })

    if (key === 'showMedia' || key === 'device') {
      openDialog(EditItemFilesDialog, {
        maxWidth: 'lg',
        files,
        index: files.length,
        fullWidth: true,
        onDelete: (_key, currentFiles) => {
          this.setState({
            quote: updateFilesForItem({ quote: this.state.quote, options, itemKey: itemKey, files: currentFiles })
          })
        },
        onConfirm: (newFiles) => {
          if (this.handleFilesChange) {
            this.handleFilesChange(newFiles)
          }
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    } else {
      openDialog(EditIntegrationItemFilesDialog, {
        provider: key,
        maxWidth: 'lg',
        fullWidth: true,
        files: getFilesForItem({ quote, options, itemKey }),
        source: this.state.quote?.sources?.[key],
        onConfirm: (newFiles, _deletedFiles, source) => {
          if (this.handleFilesChange) {
            this.handleFilesChange(newFiles, source)
          }
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    }
  }

  public handleActionItemClick = (
    event: React.MouseEvent<HTMLElement>,
    actionName: string,
    item: RenderableItemSubstrate,
    section?: QuoteItemSection
  ) => {
    const { isInGroup } = this.props
    const { quote, quoteLabel } = this.state
    const areaKey = this.state.itemKey
    const { key } = item
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { options } = this.props.clientOptions

    if (isInGroup && ['make-bid', 'make-option', 'archive'].includes(actionName)) {
      openDialog(AlertDialog, {
        message: (
          <>
            <p>
              This Area is part of a group, so it's {quoteLabel.substrate.plural.toLowerCase()} cannot be moved
              individually.
            </p>
            <p>Ungroup it first to move {quoteLabel.substrate.plural.toLowerCase()}.</p>
          </>
        ),
        onConfirm: () => {
          dismissDialog()
        }
      })
      return
    }

    if (actionName === 'edit') {
      this.openSubstrateDialog(key)
    } else if (actionName === 'add-more') {
      this.handleNewSubstrate(event, item.section, section)
    } else if (actionName === 'make-bid') {
      this.setState({ quote: moveAreaSubstrate({ quote, options, areaKey, key, section: 'bid' }) })
    } else if (actionName === 'make-option') {
      this.setState({ quote: moveAreaSubstrate({ quote, options, areaKey, key, section: 'options' }) })
    } else if (actionName === 'move-to-added-options') {
      this.setState({ quote: moveAreaSubstrate({ quote, options, areaKey, key, section: 'added-options' }) })
    } else if (actionName === 'make-pending') {
      this.setState({ quote: moveAreaSubstrate({ quote, options, areaKey, key, section: 'pending' }) })
    } else if (actionName === 'archive') {
      this.setState({ quote: moveAreaSubstrate({ quote, options, areaKey, key, section: 'archived' }) })
    } else if (actionName === 'delete') {
      this.setState({ quote: removeAreaSubstrate({ quote, options, areaKey, key }) })
    } else if (actionName === 'adjust-totals') {
      this.handleEditTotals()
    } else if (actionName === 'copy') {
      this.setState({ quote: copyAreaSubstrate({ quote, options, areaKey, key }) })
    }
  }

  public handleReorder = (args: { items: any[]; section: QuoteItemSection }) => {
    const { items } = args
    const { options } = this.props.clientOptions
    const { quote } = this.state
    this.setState({
      quote: reorderAreaSubstrates({ quote, options, areaKey: this.state.itemKey, areaSubstrates: items })
    })
  }

  public handleSubstrateClick = (event: React.MouseEvent<HTMLElement>, item: RenderableItemSubstrate) => {
    this.openSubstrateDialog(item.key)
  }

  public handleNavArrowClick = (itemKey: string) => {
    const { quote } = this.state
    if (this.props.onConfirm) {
      this.props.onConfirm(quote, itemKey)
    }
  }

  public handleDialogConfirm = () => {
    const { dialogStackContext } = this.props
    const { options } = this.props.clientOptions
    const { quote, itemKey, quoteLabel } = this.state

    if (this.props.onConfirm) {
      const rawArea = quote.areas[itemKey]
      const hiddenWarning = this.checkHiddenSubstrates(rawArea)
      const emptyWarning = this.checkEmptyArea(rawArea)

      // Dont allow areas without either a name or substrates. If just name will be visible in work order only.
      if (emptyWarning) {
        dialogStackContext.openDialog(ConfirmationDialog, {
          title: 'Unable to create Area',
          scary: true,
          message: 'Area must include a name or a surface.',
          onConfirm: () => {
            dialogStackContext.dismissDialog()
          }
        })
      } else {
        // Calculate quote before handing back to make sure if any substrates in this area that
        // area already part of a group will be combined with the existing grouped substrates,
        // adjusting price & description automatically
        const calculatedQuote = calculate(quote, options)
        if (hiddenWarning) {
          dialogStackContext.openDialog(ConfirmationDialog, {
            scary: true,
            message:
              hiddenWarning === 'all-hidden'
                ? `Warning: All ${quoteLabel.substrate.plural.toLowerCase()} within this area are hidden from the customer. This area will not be visible on your ${quoteLabel.quote.value.toLowerCase()}. Click the ${quoteLabel.workOrder.value.toLowerCase()} tab to view the area and unhide ${quoteLabel.substrate.plural.toLowerCase()}.`
                : `Warning: This area does not have any ${quoteLabel.substrate.plural.toLowerCase()} and will not be visible on your ${quoteLabel.quote.value.toLowerCase()}. Click the ${quoteLabel.workOrder.value.toLowerCase()} tab to view the area and add ${quoteLabel.substrate.plural.toLowerCase()}.`,
            onConfirm: () => {
              this.props.onConfirm(calculatedQuote)
            },
            onCancel: () => {
              dialogStackContext.dismissDialog()
            }
          })
        } else {
          this.props.onConfirm(calculatedQuote)
        }
      }
    }
  }

  public checkEmptyArea = (area: QuoteArea): boolean => {
    // If no area_label and no substrates, area becomes invisible on work order & quote, need to prevent this
    const noSubstrates = Object.values(area.substrates).every((subSection) => subSection.length === 0)
    const hasLabel = (area?.area_label?.value ?? '') !== ''

    return noSubstrates && !hasLabel
  }

  public checkHiddenSubstrates = (area: QuoteArea): 'all-hidden' | 'no-substrates' => {
    const substrates = Object.keys(area.substrates)
      .map((key) => {
        return area.substrates[key]
      })
      .flat()

    const hasVisibleVisibleSubstrates = substrates.some((substrate) => !substrate.workOrderOnly)
    const priceOverridden = !!area.totals.override_price
    const descriptionOverridden = !!area.substrate_string.use_custom
    if (!hasVisibleVisibleSubstrates && !priceOverridden && !descriptionOverridden) {
      return substrates.length > 0 ? 'all-hidden' : 'no-substrates'
    }
  }

  public handleDialogCancel = () => {
    const { dialogStackContext, onCancel } = this.props
    const { quote, cleanQuote } = this.state
    const hasChanges = !isLooselyEqual(quote, cleanQuote)

    if (hasChanges) {
      dialogStackContext.openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dialogStackContext.dismissDialog() // Dismiss confirmation dialog
          dialogStackContext.dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel(quote)
      } else {
        dialogStackContext.dismissAllDialogs()
      }
    }
  }
}

export default withStyles(styles)(withSnackbar(withDialogStackContext(withClientOptions(withUser(EditAreaDialog)))))
